import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { ChevronDown, Flag } from 'lucide-react'
import { PlayHeadline } from '@/components/brand/PlayHeadline'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { ExploreEventCard, ExploreEventSkeletons } from '@/components/explore/ExploreEventCard'
import { Button, ButtonLink, EmptyState, Notice } from '@/components/play'
import { isViewer } from '@/components/auth/isViewer'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useExploreAudience } from '@/hooks/useExploreAudience'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import type { ExploreAudience, ExploreEvent, ExploreEventsResponse } from '@/types/explore'
import '@/components/site-page.css'
import './ExplorePage.css'

type Timing = 'upcoming' | 'past'

/** What a section is called, by a heading, a screen reader and its failure. */
const COPY: Record<Timing, { title: string; list: string; failed: string }> = {
  upcoming: { title: 'Upcoming', list: 'Upcoming events', failed: 'We couldn’t load upcoming events.' },
  past: { title: 'Past', list: 'Past events', failed: 'We couldn’t load past events.' },
}

/**
 * Listed events a page at a time (GET /public/events, section 6.2): on now and to come, soonest first, without `when`,
 * and past ones, latest first, with it.
 */
function fetchEvents(timing: Timing, cursor?: string) {
  const params = { ...(timing === 'past' ? { when: 'past' } : {}), ...(cursor ? { cursor } : {}) }
  return api.get<ExploreEventsResponse>('/public/events', Object.keys(params).length ? params : undefined)
}

/** Where focus goes once a load that the person asked for has finished: an event's card, or what the empty state offers. */
type FocusTarget = { event: string } | 'empty' | null

/**
 * One list of events as cards, with Show more for the next page. The control that retries a failed load stays in place,
 * busy, while it runs, so focus never falls to the page. `empty` is what it says with nothing listed; without it the
 * section says nothing then, or while it first loads.
 */
function ExploreSection({ timing, audience, empty, emptyAction }: {
  timing: Timing
  audience: ExploreAudience
  empty?: ReactNode
  emptyAction?: RefObject<HTMLAnchorElement | null>
}) {
  const copy = COPY[timing]
  const titleId = useId()
  const [events, setEvents] = useState<ExploreEvent[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // retrying keeps the failure notice, with its Try again busy, until the retry answers.
  const [status, setStatus] = useState<'loading' | 'retrying' | 'ready' | 'failed'>('loading')
  const [attempt, setAttempt] = useState(0)
  const [failures, setFailures] = useState(0)
  const [more, setMore] = useState<'idle' | 'loading' | 'failed'>('idle')
  const links = useRef(new Map<string, HTMLAnchorElement>())
  const focusOnLoad = useRef<FocusTarget>(null)

  useEffect(() => {
    let cancelled = false
    fetchEvents(timing)
      .then((res) => {
        if (cancelled) return
        const loaded = res.data ?? []
        // A retry's Try again goes with its notice, so focus moves on to what loaded.
        if (attempt > 0) focusOnLoad.current = loaded[0] ? { event: loaded[0].id } : 'empty'
        setEvents(loaded)
        setNextCursor(res.meta?.next_cursor ?? null)
        setStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setFailures((count) => count + 1)
        setStatus('failed')
      })
    return () => { cancelled = true }
  }, [attempt, timing])

  // A load the person asked for hands focus to what it brought: Show more to its first event, even when the button goes
  // with the last page; a retry to the first event, or to what the empty state offers. Before paint, so focus never
  // rests on the page while the control that had it goes.
  useLayoutEffect(() => {
    const target = focusOnLoad.current
    if (!target) return
    focusOnLoad.current = null
    ;(target === 'empty' ? emptyAction?.current : links.current.get(target.event))?.focus()
  }, [events, status, emptyAction])

  const retry = () => {
    setStatus('retrying')
    setAttempt((current) => current + 1)
  }

  const showMore = useCallback(async () => {
    if (!nextCursor) return
    setMore('loading')
    try {
      const res = await fetchEvents(timing, nextCursor)
      const added = (res.data ?? []).filter((event) => !events.some((shown) => shown.id === event.id))
      focusOnLoad.current = added[0] ? { event: added[0].id } : null
      setEvents([...events, ...added])
      setNextCursor(res.meta?.next_cursor ?? null)
      setMore('idle')
    } catch {
      setMore('failed')
    }
  }, [events, nextCursor, timing])

  const failed = status === 'failed' || status === 'retrying'
  const listed = status === 'ready' && events.length > 0
  if (status === 'loading' && timing === 'past') return null
  if (status === 'ready' && !listed) return empty ?? null

  return (
    <section className="play-explore__section" aria-labelledby={titleId}>
      <h2 id={titleId} className="play-explore__title">{copy.title}</h2>
      {status === 'loading' && <ExploreEventSkeletons />}

      {failed && (
        // An alert once a retry has failed too. The keyed message is new text each time, so every failure is
        // announced, while the notice and its Try again stay mounted.
        <Notice tone="error" alert={failures > 1} action={<Button loading={status === 'retrying'} onClick={retry}>Try again</Button>}>
          <span key={failures}>{copy.failed}</span>
        </Notice>
      )}

      {listed && (
        <>
          {/* role="list" because VoiceOver drops list semantics from an unstyled list. */}
          <ul className="explore-grid" role="list" aria-label={copy.list}>
            {events.map((event) => (
              <li key={event.id}>
                <ExploreEventCard
                  event={event}
                  audience={audience}
                  ref={(node) => {
                    if (node) links.current.set(event.id, node)
                    else links.current.delete(event.id)
                  }}
                />
              </li>
            ))}
          </ul>
          {/* Show more stays mounted under a failure, as the way to try again, so it keeps focus. */}
          {more === 'failed' && (
            <Notice tone="error" alert className="play-explore__more-error">We couldn’t load more events. Try again.</Notice>
          )}
          {nextCursor && (
            <div className="play-explore__more">
              <Button variant="secondary" loading={more === 'loading'} onClick={showMore}>
                <ChevronDown aria-hidden="true" />
                Show more
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Events (/explore): every listed event on now or still to come, then those that have ended, each with its hosts as
 * cards below it and who is watching each host now. Nothing to come asks people to host a watch-along; a host can also
 * go to their events to list one. Docs: docs/features/explore.md.
 */
export function ExplorePage() {
  const { isAuthenticated, authReady, user, organization } = useAuth()
  const host = isAuthenticated && authReady && !isViewer(organization, user)
  const hostAction = useRef<HTMLAnchorElement>(null)
  const audience = useExploreAudience()
  useDocumentTitle('Events · StreamBridge')

  return (
    <div className="play-theme site-page play-site">
      <a className="site-page__skip" href="#explore-main">Skip to content</a>
      <SiteHeader />
      <main id="explore-main" className="site-page__main">
        <div className="site-page__intro play-explore__intro">
          <p className="site-page__eyebrow">Events</p>
          <h1 className="site-page__title"><PlayHeadline words={['On now.', 'Up next.']} /></h1>
          <p className="site-page__lead">Pick a race. Bring your people.</p>
        </div>

        <ExploreSection
          timing="upcoming"
          audience={audience}
          emptyAction={hostAction}
          empty={
            <EmptyState
              icon={<Flag />}
              title="Nothing on the start line yet"
              description="Races show up here once their hosts list them."
              action={
                <>
                  <ButtonLink to="/host" ref={hostAction}>Host a watch-along</ButtonLink>
                  {host && <ButtonLink to="/app" variant="secondary">Your events</ButtonLink>}
                </>
              }
            />
          }
        />
        <ExploreSection timing="past" audience={audience} />
      </main>
      <SiteFooter />
    </div>
  )
}
